import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { hatchingAPI } from '../../services/api';
import { parseDateOnly, toLocalISO } from '../../utils/dates';

const incubationDayFrom = (setDate) => {
  const set = parseDateOnly(setDate);
  const today = parseDateOnly(new Date());
  if (!set || !today) return null;
  return Math.round((today.getTime() - set.getTime()) / 86400000);
};

const BatchDetail = () => {
  const { batchId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [batch, setBatch] = useState(null);
  const [saving, setSaving] = useState(false);
  const [showCreateCohort, setShowCreateCohort] = useState(false);
  const [candlingDay, setCandlingDay] = useState(null);
  const [showHatchForm, setShowHatchForm] = useState(false);
  const [candlingForm, setCandlingForm] = useState({ fertile: '', infertile: '' });
  const [hatchForm, setHatchForm] = useState({ chicksHatched: '', lateDeaths: '0' });

  const load = async () => {
    try {
      setLoading(true);
      const res = await hatchingAPI.getBatch(batchId);
      setBatch(res.data?.batch || null);
    } catch (e) {
      alert(e.response?.data?.message || 'Failed to load batch');
      navigate('/hatching');
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [batchId]);

  const day = incubationDayFrom(batch?.setDate);
  const finished = ['hatched', 'brooding', 'closed'].includes(batch?.status);
  const isBroody = batch?.incubationType === 'broody';
  const canCandling = (target) => !finished && day != null && day >= target;
  const canLockdown = !finished && !isBroody && day != null && day >= 18;
  const canHatch = !['brooding', 'closed'].includes(batch?.status) && day != null && day >= 20;
  const canCreateCohort = Number(batch?.chicksHatched || 0) > 0 && batch?.status !== 'brooding';

  const availability = (target) => {
    if (day == null) return '';
    if (day < target) return `Available from day ${target}. Today is day ${day}.`;
    return `Today is day ${day}.`;
  };

  const submitCandling = async (e) => {
    e.preventDefault();
    try {
      setSaving(true);
      await hatchingAPI.recordCandling(batch.id, {
        day: candlingDay,
        fertile: Number(candlingForm.fertile || 0),
        infertile: Number(candlingForm.infertile || 0)
      });
      setCandlingDay(null);
      setCandlingForm({ fertile: '', infertile: '' });
      await load();
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to record candling');
    } finally { setSaving(false); }
  };

  const doLockdown = async () => {
    try {
      setSaving(true);
      await hatchingAPI.lockdown(batch.id);
      await load();
    } catch (e) {
      alert(e.response?.data?.message || 'Failed to set lockdown');
    } finally { setSaving(false); }
  };

  const submitHatch = async (e) => {
    e.preventDefault();
    try {
      setSaving(true);
      await hatchingAPI.recordHatch(batch.id, {
        chicksHatched: Number(hatchForm.chicksHatched || 0),
        lateDeaths: Number(hatchForm.lateDeaths || 0)
      });
      setShowHatchForm(false);
      await load();
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to record hatch');
    } finally { setSaving(false); }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Batch Detail</h1>
          <p className="text-gray-600">Incubation tracking and actions</p>
        </div>
        <button className="btn-outline" onClick={() => navigate('/hatching')}>Back</button>
      </div>

      {loading ? (
        <div className="rounded-xl border border-gray-200 bg-white shadow-sm p-6 text-gray-500">Loading...</div>
      ) : !batch ? (
        <div className="rounded-xl border border-gray-200 bg-white shadow-sm p-6 text-gray-500">Batch not found.</div>
      ) : (
        <div className="space-y-6">
          <div className="rounded-xl border border-gray-200 bg-white shadow-sm p-6">
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
              <KV label="Type" value={batch.incubationType} />
              <KV label="Set Date" value={batch.setDate} />
              <KV label="Due Date" value={batch.dueDate} />
              <KV label="Eggs Set" value={batch.eggsSet} />
              <KV label="Eggs for Hatch" value={batch.eggsForHatch ?? '—'} />
              <KV label="Infertile" value={batch.eggsInfertile ?? '—'} />
              <KV label="Hatched" value={batch.chicksHatched ?? '—'} />
              <KV label="Status" value={batch.status} />
              <KV label="Breed" value={batch.breed || '—'} />
            </div>
            {day != null && (
              <p className="text-sm text-gray-500 mt-4">Today is incubation day {day}.</p>
            )}
          </div>

          <div className="rounded-xl border border-gray-200 bg-white shadow-sm p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Actions</h3>
            <div className="space-y-3">
              <ActionCard
                title="Record candling (Day 7)"
                hint="Identify infertile/clears early to reduce contamination."
                availability={availability(7)}
                onAsk={() => navigate(`/ai-assistant?ask=${encodeURIComponent('What exactly should I check and remove during candling on day 7?')}`)}
              >
                <button className="btn-outline" disabled={saving || !canCandling(7)} onClick={() => { setCandlingDay(7); setCandlingForm({ fertile: batch.eggsForHatch ?? '', infertile: batch.eggsInfertile ?? '' }); }}>Record</button>
              </ActionCard>

              <ActionCard
                title="Record candling (Day 14)"
                hint="Confirm development progress and remove late clears."
                availability={availability(14)}
                onAsk={() => navigate(`/ai-assistant?ask=${encodeURIComponent('How do I interpret candling results on day 14?')}`)}
              >
                <button className="btn-outline" disabled={saving || !canCandling(14)} onClick={() => { setCandlingDay(14); setCandlingForm({ fertile: batch.eggsForHatch ?? '', infertile: '' }); }}>Record</button>
              </ActionCard>

              {!isBroody && (
                <ActionCard
                  title="Set lockdown (Day 18)"
                  hint="Stop turning and raise humidity (65–70%) so chicks can position for pipping."
                  availability={availability(18)}
                  onAsk={() => navigate(`/ai-assistant?ask=${encodeURIComponent('What are the exact steps for lockdown on day 18?')}`)}
                >
                  <button className="btn-outline" disabled={saving || !canLockdown || batch.status === 'lockdown'} onClick={doLockdown}>
                    {batch.status === 'lockdown' ? 'Already in lockdown' : 'Record'}
                  </button>
                </ActionCard>
              )}

              <ActionCard
                title="Record hatch (Day 21)"
                hint="Count saleable chicks, note late deaths, and move dry chicks to the brooder."
                availability={availability(20)}
                onAsk={() => navigate(`/ai-assistant?ask=${encodeURIComponent('Hatch day: how to count chicks and reduce early chick mortality?')}`)}
              >
                <button className="btn-outline" disabled={saving || !canHatch} onClick={() => { setShowHatchForm(true); setHatchForm({ chicksHatched: batch.chicksHatched ?? '', lateDeaths: batch.lateDeaths ?? '0' }); }}>Record</button>
                <button className="btn-success" disabled={!canCreateCohort} onClick={() => setShowCreateCohort(true)}>Create chick flock</button>
              </ActionCard>
            </div>
          </div>

          {candlingDay && (
            <form onSubmit={submitCandling} className="rounded-xl border border-amber-200 bg-amber-50 p-4 space-y-3">
              <h4 className="font-semibold text-gray-900">Day {candlingDay} candling</h4>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="label">Fertile eggs</label>
                  <input type="number" min="0" required className="input" value={candlingForm.fertile} onChange={(e) => setCandlingForm((p) => ({ ...p, fertile: e.target.value }))} />
                </div>
                <div>
                  <label className="label">Infertile / clears</label>
                  <input type="number" min="0" required className="input" value={candlingForm.infertile} onChange={(e) => setCandlingForm((p) => ({ ...p, infertile: e.target.value }))} />
                </div>
              </div>
              <div className="flex justify-end gap-2">
                <button type="button" className="btn-outline" onClick={() => setCandlingDay(null)}>Cancel</button>
                <button type="submit" className="btn-primary" disabled={saving}>{saving ? 'Saving…' : 'Save candling'}</button>
              </div>
            </form>
          )}

          {showHatchForm && (
            <form onSubmit={submitHatch} className="rounded-xl border border-green-200 bg-green-50 p-4 space-y-3">
              <h4 className="font-semibold text-gray-900">Record hatch</h4>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="label">Chicks hatched</label>
                  <input type="number" min="0" required className="input" value={hatchForm.chicksHatched} onChange={(e) => setHatchForm((p) => ({ ...p, chicksHatched: e.target.value }))} />
                </div>
                <div>
                  <label className="label">Late deaths</label>
                  <input type="number" min="0" className="input" value={hatchForm.lateDeaths} onChange={(e) => setHatchForm((p) => ({ ...p, lateDeaths: e.target.value }))} />
                </div>
              </div>
              <div className="flex justify-end gap-2">
                <button type="button" className="btn-outline" onClick={() => setShowHatchForm(false)}>Cancel</button>
                <button type="submit" className="btn-primary" disabled={saving}>{saving ? 'Saving…' : 'Save hatch'}</button>
              </div>
            </form>
          )}

          {showCreateCohort && (
            <CreateCohortModal batch={batch} onClose={() => setShowCreateCohort(false)} onCreated={() => { setShowCreateCohort(false); load(); }} />
          )}
        </div>
      )}
    </div>
  );
};

const ActionCard = ({ title, hint, availability, onAsk, children }) => (
  <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 p-3 rounded-lg border border-gray-200">
    <div className="min-w-0">
      <div className="font-medium text-gray-900">{title}</div>
      <div className="text-xs text-gray-600">{hint}</div>
      <div className="mt-2 flex flex-wrap gap-2">{children}<button type="button" className="btn-primary" onClick={onAsk}>Ask AI</button></div>
    </div>
    <div className="text-xs text-gray-500 sm:text-right shrink-0">{availability}</div>
  </div>
);

const KV = ({ label, value }) => (
  <div>
    <div className="text-gray-500 text-xs uppercase tracking-wide">{label}</div>
    <div className="text-gray-900 font-medium capitalize">{String(value)}</div>
  </div>
);

const CreateCohortModal = ({ batch, onClose, onCreated }) => {
  const [saving, setSaving] = useState(false);
  const onSubmit = async (e) => {
    e.preventDefault();
    const form = new FormData(e.currentTarget);
    const payload = {
      name: form.get('name'),
      type: form.get('type'),
      subtype: form.get('subtype') || undefined,
      breed: form.get('breed') || undefined,
      managementSystem: form.get('managementSystem') || undefined
    };
    try {
      setSaving(true);
      await hatchingAPI.createCohort(batch.id, payload);
      onCreated();
    } catch (e) {
      alert(e.response?.data?.message || 'Failed to create flock');
    } finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-2 sm:p-4 z-50">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg border border-gray-200 max-h-[90vh] overflow-y-auto">
        <div className="px-6 py-4 bg-gradient-to-r from-primary-600 to-secondary-500 text-white rounded-t-2xl flex items-center justify-between">
          <h3 className="font-semibold">Create Chick Flock</h3>
          <button className="inline-flex items-center px-3 py-1 rounded-md bg-white text-primary-700 hover:bg-primary-50 shadow" onClick={onClose}>Close</button>
        </div>
        <form onSubmit={onSubmit} className="p-4 sm:p-6 space-y-4">
          <p className="text-sm text-gray-600">{batch.chicksHatched} chicks will be added as a new flock.</p>
          <div>
            <label className="label">Name *</label>
            <input name="name" required className="input" defaultValue={`Hatch ${toLocalISO(batch.dueDate || batch.setDate)}`} />
          </div>
          <div>
            <label className="label">Type *</label>
            <select name="type" required className="input">
              <option value="broiler">Broiler</option>
              <option value="layer">Layer</option>
              <option value="dual">Kienyeji (Dual-purpose)</option>
            </select>
          </div>
          <div>
            <label className="label">Subtype</label>
            <select name="subtype" className="input">
              <option value="">—</option>
              <option value="kienyeji_improved">Improved Kienyeji</option>
              <option value="kienyeji_pure">Pure Kienyeji</option>
            </select>
          </div>
          <div>
            <label className="label">Breed</label>
            <input name="breed" className="input" defaultValue={batch.breed || ''} />
          </div>
          <div>
            <label className="label">Management System</label>
            <select name="managementSystem" className="input">
              <option value="">—</option>
              <option value="intensive">Intensive</option>
              <option value="semi_intensive">Semi‑intensive</option>
              <option value="free_range">Free‑range</option>
            </select>
          </div>
          <div className="pt-2 flex items-center justify-end gap-2">
            <button type="button" className="btn-outline" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn-success" disabled={saving}>{saving ? 'Creating...' : 'Create flock'}</button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default BatchDetail;
